import { useCinematic } from "@/data/cinematicMode/context";
import { Button } from "@/components/ui/button";
import {
  Eye, TrendingUp, DollarSign, ChevronRight, CheckCircle2, Lightbulb, Sparkles, MousePointerClick,
} from "lucide-react";
import { cn } from "@/lib/utils";

export function NarratorPanel() {
  const { state, currentScene, scenes, markSceneComplete, nextScene } = useCinematic();

  if (!state.active || !state.narratorOpen) return null;

  const isComplete = state.completedScenes[currentScene.id];
  const isLast = state.currentSceneIndex === scenes.length - 1;

  return (
    <div className="border-b bg-card/80 backdrop-blur-sm">
      <div className="px-4 md:px-6 py-3">
        {/* Top row: scene context + action */}
        <div className="flex items-start gap-4 mb-3">
          <div className="flex items-center gap-2 shrink-0 pt-0.5">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Narrator
            </span>
          </div>
          <p className="flex-1 text-[13px] text-foreground/90 leading-relaxed font-medium">
            {currentScene.narration}
          </p>
          <div className="shrink-0">
            {!isComplete ? (
              <Button
                size="sm"
                variant="outline"
                className="text-[11px] h-7 gap-1.5"
                onClick={() => markSceneComplete(currentScene.id)}
              >
                <CheckCircle2 className="h-3 w-3" />
                Complete
              </Button>
            ) : !isLast ? (
              <Button
                size="sm"
                className="text-[11px] h-7 gap-1.5"
                onClick={nextScene}
              >
                Next Scene
                <ChevronRight className="h-3 w-3" />
              </Button>
            ) : null}
          </div>
        </div>

        {/* Bottom row: 4 insight cards in a horizontal strip */}
        <div className="grid grid-cols-4 gap-3">
          <InsightCard
            icon={Eye}
            label="What's Happening"
            color="text-info"
            bgColor="bg-info/5 border-info/10"
            content={currentScene.whatHappens}
          />
          <InsightCard
            icon={Lightbulb}
            label="Why It Matters"
            color="text-warning"
            bgColor="bg-warning/5 border-warning/10"
            content={currentScene.whyItMatters}
          />
          <InsightCard
            icon={DollarSign}
            label="Business Impact"
            color="text-success"
            bgColor="bg-success/5 border-success/10"
            content={currentScene.businessImpact}
          />
          <InsightCard
            icon={MousePointerClick}
            label="Try This"
            color="text-primary"
            bgColor="bg-primary/5 border-primary/15"
            content={currentScene.interactionHint}
          />
        </div>
      </div>
    </div>
  );
}

function InsightCard({
  icon: Icon,
  label,
  color,
  bgColor,
  content,
}: {
  icon: React.ElementType;
  label: string;
  color: string;
  bgColor: string;
  content: string;
}) {
  return (
    <div className={cn("rounded-lg border p-2.5", bgColor)}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={cn("h-3 w-3 shrink-0", color)} />
        <span className={cn("text-[9px] font-bold uppercase tracking-wider", color)}>
          {label}
        </span>
      </div>
      <p className="text-[11px] text-foreground/70 leading-relaxed line-clamp-3">
        {content}
      </p>
    </div>
  );
}
